import { Controller } from '@hotwired/stimulus'

/**
 * Read-more controller with accessibility enhancements
 * - Updates aria-expanded dynamically
 * - Manages focus properly
 * - Provides keyboard support
 * - Screen reader announcements
 */
export default class extends Controller {
  static targets = ["content", "button"]
  static values = {
    moreText: { type: String, default: "Read more" },
    lessText: { type: String, default: "Read less" }
  }
  
  connect() {
    this.open = false
    
    // Set initial state
    this.contentTarget.style.setProperty('--read-more-line-clamp', '2')
    
    // Ensure button has proper initial ARIA attributes
    if (this.hasButtonTarget) {
      this.buttonTarget.textContent = this.moreTextValue
      this.buttonTarget.setAttribute('aria-expanded', 'false')
      
      // Generate unique ID for content if not present
      if (!this.contentTarget.id) {
        this.contentTarget.id = `read-more-content-${Math.random().toString(36).substr(2, 9)}`
      }
      
      // Link button to content
      this.buttonTarget.setAttribute('aria-controls', this.contentTarget.id)
    }
  }

  toggle(event) {
    event.preventDefault()
    
    this.open = !this.open
    const button = this.buttonTarget

    if (this.open) {
      // Expanded state
      button.textContent = this.lessTextValue
      button.setAttribute('aria-expanded', 'true')
      this.contentTarget.style.setProperty('--read-more-line-clamp', '999')
      
      // Announce to screen readers
      this.announceChange('Description expanded')
    } else {
      // Collapsed state
      button.textContent = this.moreTextValue
      button.setAttribute('aria-expanded', 'false')
      this.contentTarget.style.setProperty('--read-more-line-clamp', '2')
      
      // Announce to screen readers
      this.announceChange('Description collapsed')
    }
    
    // Keep focus on button for keyboard users
    button.focus()
  }

  /**
   * Announce state changes to screen readers using a live region
   */
  announceChange(message) {
    // Create or reuse live region
    let liveRegion = document.getElementById('read-more-announcer')
    
    if (!liveRegion) {
      liveRegion = document.createElement('div')
      liveRegion.id = 'read-more-announcer'
      liveRegion.className = 'visually-hidden'
      liveRegion.setAttribute('aria-live', 'polite')
      liveRegion.setAttribute('aria-atomic', 'true')
      document.body.appendChild(liveRegion)
    }
    
    // Clear and set new message
    liveRegion.textContent = ''
    setTimeout(() => {
      liveRegion.textContent = message
    }, 100)
  }
}
